import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Wallet } from "lucide-react";
import SEO from "@/components/SEO";

export default function Login() {
  const { login, user } = useAuth();
  const { settings } = usePlatform();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && user !== false) navigate("/workspace");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) {
      toast.success("Welcome back");
      navigate("/workspace");
    } else {
      toast.error("Invalid credentials");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 relative overflow-hidden">
      <SEO
        title={`Sign In — ${settings?.title || "GEMS"}`}
        description="Sign in to your GEMS account to manage group expenses, view transaction history, and settle balances."
        canonicalUrl="https://splitly.app/login"
      />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-emerald-100 blur-3xl opacity-60" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-rose-100 blur-3xl opacity-60" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex flex-col items-center gap-2 group cursor-pointer" data-testid="login-logo-link">
            <div className="w-11 h-11 rounded-2xl bg-slate-900 grid place-items-center overflow-hidden group-hover:scale-105 transition-transform">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <h1 className="heading text-4xl font-bold text-slate-900 tracking-tight group-hover:text-slate-800 transition-colors">{settings.title}</h1>
          </Link>
          <p className="text-slate-500 mt-2">{settings.tagline || "Group expenses, made honest."}</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl p-8 card-soft space-y-5 border border-slate-200/60"
          data-testid="login-form"
        >
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 h-12 rounded-2xl"
              placeholder="you@example.com"
              required
              data-testid="login-email-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Password</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
                data-testid="login-forgot-link"
              >
                Forgot?
              </Link>
            </div>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 h-12 rounded-2xl"
              required
              data-testid="login-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-center text-sm text-slate-500">
            New here?{" "}
            <Link to="/register" className="text-slate-900 font-semibold underline underline-offset-2" data-testid="login-register-link">
              Create an account
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
